import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";

import {
  HvGlobalActions,
  HvButton,
  HvScrollToHorizontal,
} from "@hitachivantara/uikit-react-core";
import { Backwards } from "@hitachivantara/uikit-react-icons";

import usePreventNavigation from "lib/hooks/usePreventNavigation";
import { OverflowTooltip } from "components/common";
import ConfirmationDialog from "components/common/ConfirmationDialog";

import useStyles from "./styles";

export interface FormSection {
  label: string;
  value: string;
}

const FormHeader: React.FC<{
  title: string | React.ReactNode;
  sections?: FormSection[];
  onCancel: () => void;
  cancelDialogLabels?: {
    title?: string;
    message?: string;
    confirm?: string;
    cancel?: string;
    close?: string;
  };
  icon?: React.ReactNode;
  children?: React.ReactNode;
  saveExitVisible?: boolean;
  saveDisable?: boolean;
  onSaveExit?: () => void;
}> = (props) => {
  const { t } = useTranslation("common");

  const {
    title,
    sections,
    onCancel,
    cancelDialogLabels: {
      title: cancelDialogTitle = t("cancelDialog.title") as string,
      message: cancelDialogMessage = t("cancelDialog.message") as string,
      confirm: cancelDialogConfirm = t("cancelDialog.confirm"),
      cancel: cancelDialogCancel = t("cancelDialog.cancel"),
      close: cancelDialogClose,
    } = {},
    icon,
    children,
    saveExitVisible,
    saveDisable,
    onSaveExit,
  } = props;

  const classes = useStyles();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [, setPreventNavigation] = usePreventNavigation();
  const { isValid, isSubmitting, dirty } = useFormikContext<unknown>();
  useEffect(() => {
    setPreventNavigation(dirty);
  }, [dirty, setPreventNavigation]);

  const isSaveDisabled = saveDisable || !isValid || isSubmitting || !dirty;

  const onBackHandler = () => {
    if (dirty && !saveDisable) {
      setIsDialogOpen(true);
    } else {
      onCancel();
    }
  };

  const onCancelHandler = () => {
    if (dirty && !saveDisable) {
      setIsDialogOpen(true);
    } else {
      onCancel();
    }
  };

  const onConfirmHandler = () => {
    setIsDialogOpen(false);
    setPreventNavigation(false);
    onCancel();
  };

  const BackButton = () => (
    <HvButton aria-label={t("formHeader.back")} icon onClick={onBackHandler}>
      <Backwards />
    </HvButton>
  );

  return (
    <>
      {icon}
      <HvGlobalActions
        className={classes.zIndex}
        title={
          typeof title === "string" ? (
            <OverflowTooltip
              data={title}
              className={clsx(icon ? classes.iconTitle : classes.title)}
            />
          ) : (
            title
          )
        }
        backButton={<BackButton />}
      >
        <HvButton category="primary" type="submit" disabled={isSaveDisabled}>
          {t("formHeader.save")}
        </HvButton>
        {saveExitVisible === true && (
          <HvButton
            category="primary"
            type="button"
            onClick={onSaveExit}
            disabled={isSaveDisabled}
          >
            {t("formHeader.saveExit")}
          </HvButton>
        )}
        <HvButton category="secondary" onClick={onCancelHandler}>
          {t("formHeader.cancel")}
        </HvButton>
        {children}
      </HvGlobalActions>
      {sections && (
        <HvScrollToHorizontal
          className={clsx(classes.scrollTo, classes.zIndex)}
          options={sections}
          position="sticky"
          classes={{
            root: classes.scrollToRoot,
            positionSticky: classes.scrollToSticky,
          }}
          offset={100}
          href={false}
          scrollElementId="wis-container"
        />
      )}
      <ConfirmationDialog
        isOpen={isDialogOpen}
        onConfirm={onConfirmHandler}
        onCancel={() => setIsDialogOpen(false)}
        title={cancelDialogTitle}
        message={cancelDialogMessage}
        confirmButtonLabel={cancelDialogConfirm}
        cancelButtonLabel={cancelDialogCancel}
        closeButtonTooltip={cancelDialogClose}
      />
    </>
  );
};

export default FormHeader;
